import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  HostBinding,
  inject,
  signal,
  ViewChild,
  type OnDestroy,
  type OnInit,
} from "@angular/core";

import { DialogRef, DIALOG_DATA } from "@angular/cdk/dialog";
import { TranslateModule } from "@ngx-translate/core";
import { CariInteractiveIconComponent } from "@cariloop/ui-components";
import { TotpSetupComponent } from "./totp-setup/totp-setup.component";
import { BackupMfaCodesModalComponent } from "./backup-mfa-codes-modal/backup-mfa-codes-modal.component";

export interface MFADialogData {
  isMFAEnabled: boolean;
  mfaMethod?: "email" | "totp";
  email?: string;
}

export enum DialogState {
  Opening = "opening",
  Closing = "closing",
}

export enum MfaViewType {
  Totp = "totp",
  BackupCodes = "backup-codes",
}

export interface MfaTitles {
  [MfaViewType.Totp]?: string;
  [MfaViewType.BackupCodes]?: string;
}

export const MFA_TRANSLATION_KEYS: Partial<Record<MfaViewType, string>> = {
  [MfaViewType.Totp]: "REGISTER.TWO_STEP_VERIFICATION.TOTP_MFA_MODAL.TITLE",
  [MfaViewType.BackupCodes]:
    "REGISTER.TWO_STEP_VERIFICATION.MFA_RECOVERY_CODES_MODAL.TITLE",
};

@Component({
  selector: "app-mfa-modal",
  templateUrl: "./mfa-modal.component.html",
  styleUrls: ["./mfa-modal.component.scss"],
  imports: [
    TranslateModule,
    CariInteractiveIconComponent,
    TotpSetupComponent,
    BackupMfaCodesModalComponent,
  ],
})
export class MfaModalComponent implements OnInit, OnDestroy {
  readonly data = inject<MFADialogData>(DIALOG_DATA);
  #dialogRef = inject(DialogRef);
  #animationTimer: ReturnType<typeof setTimeout> | null = null;

  // Expose enums to template
  readonly MfaViewType = MfaViewType;
  readonly DialogState = DialogState;

  state: DialogState = DialogState.Opening;
  currentView = signal<MfaViewType>(MfaViewType.Totp);
  titles: MfaTitles = {
    [MfaViewType.Totp]: MFA_TRANSLATION_KEYS[MfaViewType.Totp],
    [MfaViewType.BackupCodes]: MFA_TRANSLATION_KEYS[MfaViewType.BackupCodes],
  };
  hasMainViewContent = false;
  hasEmailViewContent = false;
  hasTotpViewContent = false;
  hasMultipleMethods = false;
  backupCodes: string[] = [];

  titleKey = computed(() => {
    return this.titles[this.currentView()] || "";
  });

  shouldShowBackButton = computed(() => {
    if (this.currentView() === MfaViewType.BackupCodes) {
      return false;
    }

    return this.hasMultipleMethods;
  });

  @HostBinding("class.dialog--closing")
  get isDialogClosing(): boolean {
    return this.state === DialogState.Closing;
  }

  ngOnInit(): void {
    this.#dialogRef.addPanelClass("mfa-modal");
    this.#dialogRef.disableClose = true;
  }
  ngOnDestroy(): void {
    if (this.#animationTimer !== null) {
      clearTimeout(this.#animationTimer);
    }
  }

  closeModal(isMFAEnabled: boolean) {
    const isMobile = window.innerWidth < 576;
    const closeDuration = isMobile ? 300 : 75;
    if (this.#animationTimer !== null) {
      clearTimeout(this.#animationTimer);
    }
    this.state = DialogState.Closing;
    this.#animationTimer = setTimeout(
      () => this.#dialogRef.close(isMFAEnabled),
      closeDuration,
    );
  }

  public onTotpClick() {
    this.currentView.set(MfaViewType.Totp);
  }

  public nextView(viewType: MfaViewType) {
    this.currentView.set(viewType);
  }

  handleBackupCodesResult({ backupCodes }: { backupCodes: string[] }) {
    this.backupCodes = backupCodes;
    this.nextView(MfaViewType.BackupCodes);
  }

  public goBack(isMFAEnabled) {
    this.closeModal(isMFAEnabled);
  }
}
